import { COMMISSIONING_STATUS_RULES } from './rules/commissioning.status-rules';
import { CONNECTION_CHANGE_REQUEST_STATUS_RULES } from './rules/connection-change-request.status-rules';
import { FEEDER_CONTRACT_STATUS_RULES } from './rules/feeder-contract.status-rules';
import { GRID_REGISTRATION_STATUS_RULES } from './rules/grid-registration.status-rules';
import { INSTALLER_PASS_STATUS_RULES } from './rules/installer-pass.status-rules';
import { ORDER_REQUEST_STATUS_RULES } from './rules/order-request.status-rules';
import { StatusAvailabilityContext, StatusRuleSet } from './status-availability.model';
import { SelectableStatus, StatusModule } from './status.model';

/** Die eine Regelstelle je Modul (siehe `rules/`). */
export const STATUS_RULES: Record<StatusModule, StatusRuleSet> = {
  gridRegistration: GRID_REGISTRATION_STATUS_RULES,
  commissioning: COMMISSIONING_STATUS_RULES,
  feederContract: FEEDER_CONTRACT_STATUS_RULES,
  orderRequests: ORDER_REQUEST_STATUS_RULES,
  installerPassList: INSTALLER_PASS_STATUS_RULES,
  connectionChangeRequest: CONNECTION_CHANGE_REQUEST_STATUS_RULES
};

export const CURRENT_STATUS_SUFFIX = ' (aktueller Status)';

/** Ist `statusManagement.<modul>.hideUnreachableStatuses` für den Mandanten aktiv? */
export function isHidingActive(module: StatusModule, ctx: StatusAvailabilityContext): boolean {
  return !!ctx.config.statusManagement?.[module]?.hideUnreachableStatuses;
}

/** Unter „Optionale Status“ freigegebene Werte des Moduls (`statusManagement.<modul>.optionalStatuses`). */
export function optionalStatuses(module: StatusModule, ctx: StatusAvailabilityContext): ReadonlySet<string> {
  return new Set(ctx.config.statusManagement?.[module]?.optionalStatuses ?? []);
}

/**
 * Angebotene Status: ohne aktive Konfig alle, sonst ohne die, die der Mandant nicht erreicht. Optionale
 * Status sind dann immer dabei, unabhängig von ihrer Freischaltbedingung.
 */
export function filterSelectable<T extends { value: string }>(
  module: StatusModule,
  statuses: readonly T[],
  ctx: StatusAvailabilityContext
): T[] {
  if (!isHidingActive(module, ctx)) {
    return [...statuses];
  }
  const rules = STATUS_RULES[module];
  const optional = optionalStatuses(module, ctx);
  return statuses.filter((status) => optional.has(status.value) || isSelectable(rules, status.value, ctx));
}

/**
 * Stellt den aktuellen Status als deaktivierte Option voran, wenn er zur Stammliste gehört, aber nicht
 * angeboten wird. Ohne aktive Konfig sind alle Stammwerte angeboten, die Liste bleibt dann unverändert;
 * unbekannte Werte werden nicht ergänzt.
 */
export function withCurrentStatus(
  selectable: readonly SelectableStatus[],
  statuses: readonly SelectableStatus[],
  current: string | null | undefined
): SelectableStatus[] {
  if (current == null || selectable.some((s) => s.value === current)) {
    return [...selectable];
  }
  const known = statuses.find((s) => s.value === current);
  if (!known) {
    return [...selectable];
  }
  return [
    { ...known, displayName: (known.displayName ?? current) + CURRENT_STATUS_SUFFIX, disabled: true },
    ...selectable
  ];
}

function isSelectable(rules: StatusRuleSet, value: string, ctx: StatusAvailabilityContext): boolean {
  if (!Object.prototype.hasOwnProperty.call(rules.blacklist, value)) {
    return true;
  }
  const unlock = rules.blacklist[value];
  return !!unlock && unlock(ctx);
}
